import { useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { Seo, serviceSchema, breadcrumbSchema } from "@/lib/seo";
import { SectionHead } from "@/pages/Home";
import {
  FileText, Compass, Globe, Palette, Server, Search, Rocket, ArrowRight, Check,
  Loader2, X, Send, PhoneCall,
} from "lucide-react";

const DELIVERABLES = [
  { icon: FileText, t: "Business Plan", d: "A complete business plan with financials — built from your Business Profile, not a blank page." },
  { icon: Compass, t: "AI Business Coach", d: "A guided, one-question-at-a-time coach that helps you think through every part of your business." },
  { icon: Globe, t: "Professional Website", d: "A fast, mobile-friendly website designed to win customers and grow with you." },
  { icon: Palette, t: "Branding", d: "A cohesive look — logo, colors and voice — so your business feels established from day one." },
  { icon: Server, t: "Domain & Hosting", d: "We set up your domain and host your site, keeping it secure, fast and always online." },
  { icon: Search, t: "SEO Setup", d: "The technical foundation so customers can actually find you on Google and Bing." },
  { icon: Rocket, t: "Launch Support", d: "We're with you through go-live — testing, connecting the pieces and getting you live with confidence." },
];

const STEPS = [
  { n: "01", t: "Idea", d: "You bring the vision. We capture it with the AI Coach and turn it into your Business Profile." },
  { n: "02", t: "Build", d: "We craft your plan, website, branding and SEO — you review and approve along the way." },
  { n: "03", t: "Launch", d: "We connect your domain, go live, and support you as your business opens its doors." },
];

export default function LaunchPackage() {
  const navigate = useNavigate();
  const [showForm, setShowForm] = useState(false);
  const [intent, setIntent] = useState("consult");

  const open = (which) => { setIntent(which); setShowForm(true); };

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24" data-testid="launch-package-page">
      <Seo
        title="Business Launch Package | From Idea to Launch | ATF WebWorks"
        description="Everything you need to launch your business: AI business plan, guided coach, professional website, branding, domain & hosting, SEO setup and launch support. Book a free consultation."
        path="/launch-package"
        jsonLd={[serviceSchema(), breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Business Launch Package", path: "/launch-package" }])]}
      />

      {/* Hero */}
      <SectionHead label="Business Launch Package" title="From business idea to launch — done with you." />
      <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
        className="text-gray-400 mt-5 max-w-2xl text-lg leading-relaxed">
        One partner, one path. We help you plan, build and launch a real business — combining an AI-powered
        business plan and coach with a professional website, branding, hosting and SEO. You focus on your
        vision; we handle the rest.
      </motion.p>
      <div className="mt-8 flex flex-wrap gap-4">
        <button onClick={() => open("consult")} data-testid="launch-cta-consult"
          className="inline-flex items-center gap-2 bg-gold text-navy px-8 py-3.5 font-semibold hover:bg-gold-light transition-colors">
          <PhoneCall size={17} /> Book a Free Consultation
        </button>
        <button onClick={() => open("quote")} data-testid="launch-cta-quote"
          className="inline-flex items-center gap-2 border border-white/25 text-white px-8 py-3.5 font-semibold hover:bg-white/5 transition-colors">
          Request a Custom Quote <ArrowRight size={16} />
        </button>
      </div>

      {/* What's included */}
      <section className="mt-24" data-testid="launch-deliverables">
        <h2 className="font-heading text-3xl md:text-4xl text-white">Everything you need to launch</h2>
        <p className="text-gray-400 mt-4 max-w-2xl">A complete foundation — not a pile of tools you have to figure out alone.</p>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 mt-10">
          {DELIVERABLES.map((d, i) => (
            <motion.div key={d.t} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ delay: i * 0.06 }}
              className="border border-white/10 bg-navy-800 p-6 hover:border-gold/40 transition-colors">
              <d.icon className="text-gold mb-4" size={26} />
              <h3 className="font-heading text-xl text-white mb-2">{d.t}</h3>
              <p className="text-gray-400 text-sm leading-relaxed">{d.d}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section className="mt-24" data-testid="launch-steps">
        <h2 className="font-heading text-3xl md:text-4xl text-white mb-10">How it works</h2>
        <div className="grid md:grid-cols-3 gap-8">
          {STEPS.map((s) => (
            <div key={s.n} className="border-l border-gold/40 pl-6">
              <div className="font-mono text-gold text-sm mb-2">{s.n}</div>
              <h4 className="font-heading text-xl text-white mb-2">{s.t}</h4>
              <p className="text-gray-400 leading-relaxed">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Why ATF */}
      <section className="mt-24 border border-gold/25 bg-gold/5 p-8 md:p-10" data-testid="launch-why">
        <h2 className="font-heading text-2xl md:text-3xl text-white mb-6">Why founders choose ATF WebWorks</h2>
        <div className="grid sm:grid-cols-2 gap-x-10 gap-y-3">
          {[
            "One partner from idea to launch — no juggling five vendors",
            "AI does the heavy lifting; a real human reviews and approves everything",
            "Your Business Profile is reused everywhere — no repeating yourself",
            "A clear readiness score shows exactly what's left before you go live",
            "Built to grow — add pages, SEO and features as your business expands",
            "Ongoing hosting & care so your site stays secure and current",
          ].map((f) => (
            <div key={f} className="flex items-start gap-3 text-sm text-gray-200"><Check size={16} className="text-gold shrink-0 mt-0.5" /> {f}</div>
          ))}
        </div>
      </section>

      {/* Closing CTA */}
      <div className="mt-20 text-center border border-white/10 bg-navy-800 p-10" data-testid="launch-closing-cta">
        <h3 className="font-heading text-2xl md:text-3xl text-white">Ready to turn your idea into a business?</h3>
        <p className="text-gray-400 mt-3 max-w-xl mx-auto">Book a free, no-pressure consultation. We'll map out your path to launch and answer your questions.</p>
        <div className="mt-7 flex flex-wrap gap-4 justify-center">
          <button onClick={() => open("consult")} data-testid="launch-cta-consult-2"
            className="inline-flex items-center gap-2 bg-gold text-navy px-8 py-3.5 font-semibold hover:bg-gold-light transition-colors">
            <PhoneCall size={17} /> Book a Free Consultation
          </button>
          <button onClick={() => navigate("/start")} data-testid="launch-cta-start"
            className="inline-flex items-center gap-2 border border-white/25 text-white px-8 py-3.5 font-semibold hover:bg-white/5 transition-colors">
            Start My Project <ArrowRight size={16} />
          </button>
        </div>
      </div>

      {showForm && <LeadModal intent={intent} onClose={() => setShowForm(false)} />}
    </div>
  );
}

function LeadModal({ intent, onClose }) {
  const [form, setForm] = useState({ name: "", email: "", business_name: "", goals: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const isConsult = intent === "consult";

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) { toast.error("Please add your name and email."); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) { toast.error("Please enter a valid email."); return; }
    setBusy(true);
    try {
      await api.post("/inquiries", {
        name: form.name.trim(), email: form.email.trim(),
        business_name: form.business_name.trim(),
        goals: form.goals.trim(),
        action: "save",
        cta_source: isConsult ? "launch_package_consult" : "launch_package_quote",
      });
      toast.success("Thanks! We'll reach out shortly to get you started.");
      onClose();
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose} data-testid="launch-lead-modal">
      <div className="bg-navy border border-white/10 w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-white/10">
          <span className="text-white font-heading text-lg">{isConsult ? "Book a Free Consultation" : "Request a Custom Quote"}</span>
          <button onClick={onClose} data-testid="launch-lead-close" className="text-gray-400 hover:text-white"><X size={18} /></button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-3">
          <p className="text-sm text-gray-400">{isConsult
            ? "Tell us a little about your business and we'll set up a free call to map your path to launch."
            : "Share your business goals and we'll put together a custom quote for your Business Launch Package."}</p>
          <input value={form.name} onChange={set("name")} placeholder="Your name *" data-testid="launch-lead-name"
            className="w-full bg-navy-800 border border-white/15 text-white px-3 py-2.5 text-sm focus:border-gold focus:outline-none" />
          <input value={form.email} onChange={set("email")} placeholder="Email *" type="email" data-testid="launch-lead-email"
            className="w-full bg-navy-800 border border-white/15 text-white px-3 py-2.5 text-sm focus:border-gold focus:outline-none" />
          <input value={form.business_name} onChange={set("business_name")} placeholder="Business name / idea (optional)" data-testid="launch-lead-business"
            className="w-full bg-navy-800 border border-white/15 text-white px-3 py-2.5 text-sm focus:border-gold focus:outline-none" />
          <textarea value={form.goals} onChange={set("goals")} placeholder="What do you want to launch? (optional)" rows={3} data-testid="launch-lead-notes"
            className="w-full bg-navy-800 border border-white/15 text-white px-3 py-2.5 text-sm focus:border-gold focus:outline-none resize-none" />
          <button type="submit" disabled={busy} data-testid="launch-lead-submit"
            className="w-full inline-flex items-center justify-center gap-2 bg-gold text-navy px-5 py-3 font-semibold hover:bg-gold-light transition-colors disabled:opacity-50">
            {busy ? <Loader2 className="animate-spin" size={16} /> : <Send size={16} />} {isConsult ? "Request my free call" : "Request my quote"}
          </button>
          <p className="text-[11px] text-gray-500 text-center">No payment now — this just starts the conversation.</p>
        </form>
      </div>
    </div>
  );
}
